<template>
  <section class="cfg-dashboard-root" :data-theme="theme" :class="{ 'cfg-dashboard-root--edit': editMode }">
    <div class="cfg-dashboard-layout">
      <div class="cfg-dashboard-main">
    <!-- Header -->
    <header class="cfg-dash-header">
      <div class="cfg-dash-title-wrap">
        <h1 class="cfg-dash-title">{{ title }}</h1>
        <button
          type="button"
          class="cfg-title-cog-btn"
          :class="{ 'cfg-title-cog-btn--active': editMode }"
          @click="toggleEditMode"
          :title="editMode ? 'Close editor' : 'Open editor'"
          aria-label="Toggle dashboard editor"
        >
          <i class="fas fa-cog" aria-hidden="true"></i>
        </button>
      </div>

      <div class="cfg-dash-header-spacer"></div>

      <button class="cfg-header-btn" @click="exportPDF" :disabled="exportingPdf" title="Export dashboard as PDF">
        <i class="fa fa-file-pdf" aria-hidden="true"></i>
        <span>{{ exportingPdf ? 'Exporting…' : 'Export PDF' }}</span>
      </button>

      <button class="cfg-header-btn" @click="saveConfig" title="Download the dashboard config as JSON">
        <i class="fa fa-save" aria-hidden="true"></i>
        <span>Save Config</span>
      </button>

      <!-- Theme toggle — disabled; dashboard defaults to light (re-enable if needed)
      <button class="cfg-header-btn" @click="toggleTheme" :title="theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'">
        <i :class="theme === 'dark' ? 'fa fa-sun' : 'fa fa-moon'" aria-hidden="true"></i>
        <span>{{ theme === 'dark' ? 'Light' : 'Dark' }}</span>
      </button>
      -->
    </header>

    <!-- Content -->
    <div ref="container" class="cfg-dash-container">

      <div v-if="loading" class="cfg-dash-loader">
        <div class="cfg-spinner"></div>
        <div>Loading dashboard…</div>
      </div>

      <div v-else-if="loadError" class="cfg-dash-error">
        <i class="fa fa-exclamation-triangle"></i>
        <div>
          <div class="cfg-err-title">Failed to load dashboard</div>
          <div class="cfg-err-msg">{{ loadError }}</div>
        </div>
        <button class="cfg-header-btn" @click="load">Retry</button>
      </div>

      <template v-else-if="contentReady">
        <!-- Filters -->
        <p v-if="editMode" class="cfg-edit-reorder-hint">
          <i class="fa fa-grip-vertical" aria-hidden="true"></i>
          Drag the <span class="cfg-drag-hint-icon">⋮⋮</span> handle to reorder filters, KPIs, or charts. Text stays selectable elsewhere.
        </p>

        <dashboard-filters
          :filters="mutableConfig.filters || []"
          :data="sourceData"
          :value="filterValues"
          :edit-mode="editMode"
          :selected-filter-index="filterSelectionIndex"
          @apply="onFiltersApply"
          @select-filter="onFilterSelect"
          @reorder="onReorderFilters"
        />

        <!-- KPIs -->
        <div v-if="mutableConfig.kpis && mutableConfig.kpis.length" class="cfg-kpi-row">
          <div
            v-for="(k, i) in mutableConfig.kpis"
            :key="'kpi-' + (k.label || 'k') + '-' + i"
            class="cfg-kpi-reorder-wrap"
            :class="cfgReorderWrapClass('kpi', i)"
            data-cfg-reorder="kpi"
            :data-cfg-reorder-index="i"
          >
            <button
              v-if="editMode"
              type="button"
              class="cfg-drag-handle"
              title="Drag to reorder KPI"
              aria-label="Drag to reorder KPI"
              @mousedown.prevent="onCfgDragStart('kpi', i, $event)"
            >⋮⋮</button>
            <dashboard-kpi
              :kpi="k"
              :data="filteredData"
              :index="i"
              :highlight="editMode && selection && selection.type === 'kpi' && selection.index === i"
              @click.native="onKpiClick(i, $event)"
            />
          </div>
        </div>

        <!-- Widget grid -->
        <div
          ref="chartGrid"
          class="cfg-chart-grid"
          :class="[
            'cfg-chart-grid--' + gridBreakpoint,
            { 'cfg-chart-grid--edit': editMode },
          ]"
        >
          <div
            v-for="(w, i) in mutableConfig.widgets"
            :key="w.id || 'w-' + i"
            class="cfg-grid-cell"
            :class="[
              { 'cfg-grid-cell--sel': editMode && selection && selection.type === 'widget' && selection.index === i },
              { 'cfg-grid-cell--table': w.type === 'table' },
              cfgReorderWrapClass('widget', i),
            ]"
            data-cfg-reorder="widget"
            :data-cfg-reorder-index="i"
            :style="cellStyle(w)"
            @click.capture="onWidgetCellClick(i, $event)"
          >
            <button
              v-if="editMode"
              type="button"
              class="cfg-drag-handle cfg-drag-handle--widget"
              title="Drag to reorder chart"
              aria-label="Drag to reorder chart"
              @mousedown.prevent="onCfgDragStart('widget', i, $event)"
            >⋮⋮</button>
            <dashboard-table
              v-if="w.type === 'table'"
              :widget="w"
              :data="filteredData"
            />
            <dashboard-chart
              v-else
              :widget="w"
              :data="filteredData"
              :index="i"
              :theme="themeColors"
              :compact="gridBreakpoint !== 'lg'"
            />
          </div>
        </div>
      </template>
    </div>
      </div>

      <config-dashboard-edit-sidebar
        v-show="editMode"
        :dashboard-title="title"
        :selection="selection"
        :kpi="selectedKpi"
        :widget="selectedWidget"
        :filter="selectedFilter"
        :data-columns="dataColumnNames"
        @close="closeEditor"
        @patch-title="onPatchTitle"
        @patch-kpi="onPatchKpi"
        @patch-widget="onPatchWidget"
        @patch-filter="onPatchFilter"
        @delete-kpi="onDeleteKpi"
        @delete-widget="onDeleteWidget"
        @delete-filter="onDeleteFilter"
        @add-kpi="onAddKpi"
        @add-widget="onAddWidget"
        @add-filter="onAddFilter"
      />
    </div>
  </section>
</template>

<script>
import axios from 'axios'
import _ from 'lodash'

import DashboardFilters from './DashboardFilters.vue'
import DashboardKpi from './DashboardKpi.vue'
import DashboardChart from './DashboardChart.vue'
import DashboardTable from './DashboardTable.vue'
import ConfigDashboardEditSidebar from './ConfigDashboardEditSidebar.vue'

import { readThemeColors } from './utils/theme'
import { downloadJson } from './utils/formatting'
import { reorderArray, remapIndexAfterMove } from './utils/listReorder'
import { getGridBreakpointWithHysteresis, cellStyleForWidget, reflowWidgetLayouts } from './utils/responsiveGrid'
import { widgetHasNumericYAxis, widgetHasNumericXAxis } from './utils/chartConfig'
import { toPlainObject } from './utils/plainObject'
import cfgDragReorder from './mixins/cfgDragReorder'

// const THEME_KEY = 'cfgDashboard.theme' // reserved for future theme toggle
/** Guard against huge API payloads freezing the browser during chart/filter builds. */
const MAX_DASHBOARD_ROWS = 30000
// const DASHBOARD_API_TIMEOUT_MS = 90000

export default {
  name: 'ConfigDashboard',
  mixins: [cfgDragReorder],
  components: {
    DashboardFilters, DashboardKpi, DashboardChart, DashboardTable, ConfigDashboardEditSidebar,
  },

  props: {
    // Override the route-derived submenuId
    submenuId: { type: [String, Number], default: null },
    // Override the route-derived menuId
    menuId:    { type: [String, Number], default: null },

    // Pre-loaded config/data — if both provided, no API calls are made
    config: { type: Object, default: null },
    data:   { type: Array,  default: null },

    // Fallback defaults (theme toggle disabled — always light)
    // defaultTheme: { type: String, default: 'light' },
  },

  data() {
    return {
      mutableConfig: { widgets: [], filters: [], kpis: [], layouts: {} },
      sourceData: [],
      filterValues: {},
      loading: false,
      loadError: '',

      theme: 'light',
      themeColors: {
        bg: '#ffffff', surface: '#ffffff', text: '#1e293b',
        textMuted: '#64748b', border: '#e2e8f0', accent: '#3b82f6',
      },

      exportingPdf: false,

      editMode: false,
      selection: null,

      gridContainerWidth: 1200,
      gridBreakpoint: 'lg',
      gridResizeObserver: null,
      gridResizeDebounced: null,
      contentReady: false,
    }
  },

  computed: {
    effectiveSubmenuId() {
      return this.submenuId != null ? this.submenuId : this.$route.params.submenuId
    },
    effectiveMenuId() {
      return this.menuId != null ? this.menuId : this.$route.params.menuId
    },
    title() {
      return this.mutableConfig?.title || 'Dashboard'
    },
    filteredData() {
      return this.applyFilters(this.sourceData, this.filterValues, this.mutableConfig.filters || [])
    },
    selectedKpi() {
      if (!this.selection || this.selection.type !== 'kpi') return null
      return (this.mutableConfig.kpis || [])[this.selection.index] || null
    },
    selectedWidget() {
      if (!this.selection || this.selection.type !== 'widget') return null
      return (this.mutableConfig.widgets || [])[this.selection.index] || null
    },
    filterSelectionIndex() {
      if (this.editMode && this.selection && this.selection.type === 'filter') return this.selection.index
      return -1
    },
    selectedFilter() {
      if (!this.selection || this.selection.type !== 'filter') return null
      return (this.mutableConfig.filters || [])[this.selection.index] || null
    },
    dataColumnNames() {
      const data = this.sourceData
      if (!Array.isArray(data) || !data.length) return []
      const keys = new Set()
      const sample = Math.min(data.length, 100)
      for (let i = 0; i < sample; i++) {
        const row = data[i]
        if (row && typeof row === 'object') Object.keys(row).forEach(k => keys.add(k))
      }
      return [...keys].sort()
    },
  },

  watch: {
    '$route.params.submenuId'() { this.load() },
    '$store.state.stateLoaded'(loaded) {
      if (loaded && !(this.config && Array.isArray(this.data))) this.load()
    },
    config: { immediate: false, handler() { this.hydrateFromProps() } },
    data:   { immediate: false, handler() { this.hydrateFromProps() } },
    // theme(newVal) {
    //   try { localStorage.setItem(THEME_KEY, newVal) } catch (e) { /* ignore */ }
    //   this.$nextTick(this.refreshThemeColors)
    // },
  },

  mounted() {
    this.refreshThemeColors()
    this.setupGridResizeObserver()
    if (this.config && this.data) {
      this.hydrateFromProps()
    } else {
      this.load()
    }
  },
  beforeDestroy() {
    this.teardownGridResizeObserver()
    if (this.gridResizeDebounced && this.gridResizeDebounced.cancel) {
      this.gridResizeDebounced.cancel()
    }
  },

  methods: {
    // ── Data loading ────────────────────────────────────────────────
    // loadSavedTheme() {
    //   try { return localStorage.getItem(THEME_KEY) } catch (e) { return null }
    // },

    capSourceRows(rows) {
      if (!Array.isArray(rows) || rows.length <= MAX_DASHBOARD_ROWS) return rows
      console.warn(
        `[ConfigDashboard] Truncating ${rows.length} rows to ${MAX_DASHBOARD_ROWS} for performance`
      )
      return rows.slice(0, MAX_DASHBOARD_ROWS)
    },

    findMenuMasterEntry(menuId) {
      if (!menuId) return null
      const flat = (this.$store.state.menuMasterFlat || []).find(m => m.menuId === menuId)
      if (flat) return flat
      return this.findMenuInTree(this.$store.state.menuMasterData || [], menuId)
    },

    findMenuInTree(nodes, menuId) {
      for (const node of nodes || []) {
        if (node && node.menuId === menuId) return node
        const child = this.findMenuInTree(node && node.children, menuId)
        if (child) return child
      }
      return null
    },

    parseMenuRenderJson(raw) {
      if (!raw) return null
      if (typeof raw === 'string') {
        try {
          return JSON.parse(raw)
        } catch (e) {
          console.error('[ConfigDashboard] invalid menuRenderJson JSON', e)
          return null
        }
      }
      if (typeof raw === 'object') return raw
      return null
    },

    extractDataSql(menuRenderJson) {
      const sql = menuRenderJson.datasql || menuRenderJson.dataSql || ''
      return String(sql).trim()
    },

    buildDashboardConfig(menuRenderJson) {
      const cfg = toPlainObject(menuRenderJson) || {}
      delete cfg.datasql
      delete cfg.dataSql
      delete cfg.rowsData
      delete cfg.data
      delete cfg.rows
      return cfg
    },

    extractRowsFromFetchDataResponse(payload) {
      if (Array.isArray(payload)) return payload
      if (!payload || typeof payload !== 'object') return []
      if (Array.isArray(payload.rowsData)) return payload.rowsData
      if (Array.isArray(payload.data)) return payload.data
      if (Array.isArray(payload.rows)) return payload.rows
      return []
    },

    hydrateFromProps() {
      if (this.config) this.mutableConfig = this.normalizeConfig(this.config)
      if (Array.isArray(this.data)) this.sourceData = this.data
      this.filterValues = {}
      this.contentReady = true
    },

    async load() {
      if (this.config && Array.isArray(this.data)) {
        this.hydrateFromProps()
        return
      }

      const submenuId = this.effectiveSubmenuId
      if (!submenuId) {
        this.loadError = 'Submenu id is missing from the route.'
        return
      }

      this.loadError = ''
      this.loading = true
      this.contentReady = false
      try {
        let dashboardConfig = this.config
        let dataSql = ''

        if (!dashboardConfig) {
          const menuEntry = this.findMenuMasterEntry(submenuId)
          if (!menuEntry) {
            throw new Error(`No menu found for submenu id "${submenuId}".`)
          }

          const menuRenderJson = this.parseMenuRenderJson(menuEntry.menuRenderJson)
          if (!menuRenderJson) {
            throw new Error('Dashboard configuration (menuRenderJson) is missing for this menu.')
          }

          dataSql = this.extractDataSql(menuRenderJson)
          if (!dataSql) {
            throw new Error('Dashboard SQL (datasql) is missing in menuRenderJson.')
          }

          dashboardConfig = this.buildDashboardConfig(menuRenderJson)
        } else {
          dataSql = this.extractDataSql(dashboardConfig)
        }

        this.mutableConfig = this.normalizeConfig(dashboardConfig)
        // Theme from config disabled — dashboard always uses light theme
        // if (this.mutableConfig.theme === 'dark' || this.mutableConfig.theme === 'light') {
        //   this.theme = this.mutableConfig.theme
        // }

        let rows = []
        if (Array.isArray(this.data)) {
          rows = this.data
        } else if (dataSql) {
          const headers = {
            'info-params': JSON.stringify({
              menuId: this.effectiveMenuId,
              submenuId,
            }),
            'Content-Type': 'application/json',
          }
          const dataRes = await axios.post(
            '/api/fetchData',
            { query: dataSql, vizClassName: 'Any' },
            { headers },
          )
          rows = this.extractRowsFromFetchDataResponse(dataRes.data)
        }

        this.sourceData = this.capSourceRows(rows)
        this.filterValues = {}
      } catch (err) {
        console.error('[ConfigDashboard] load failed', err)
        const apiMsg = err && err.response && err.response.data
        this.loadError = (typeof apiMsg === 'string' && apiMsg)
          || (apiMsg && apiMsg.error)
          || (err && err.message)
          || 'Unknown error'
      } finally {
        this.loading = false
        if (!this.loadError) {
          await this.$nextTick()
          await new Promise(resolve => {
            if (typeof requestAnimationFrame === 'function') requestAnimationFrame(resolve)
            else setTimeout(resolve, 0)
          })
          this.contentReady = true
        } else {
          this.contentReady = false
        }
        this.$nextTick(this.setupGridResizeObserver)
      }
      this.$nextTick(this.refreshThemeColors)
    },

    normalizeConfig(raw) {
      const cfg = toPlainObject(raw || {})
      if (!Array.isArray(cfg.widgets)) cfg.widgets = Array.isArray(cfg.charts) ? cfg.charts : []
      if (!Array.isArray(cfg.filters)) cfg.filters = []
      if (!Array.isArray(cfg.kpis)) cfg.kpis = []
      const layouts = cfg.layouts && typeof cfg.layouts === 'object' ? cfg.layouts : {}
      cfg.widgets.forEach((w, i) => {
        if (!w.id) w.id = 'w_' + i
        if (layouts[w.id]) w.layout = { ...layouts[w.id], ...(w.layout || {}) }
        if (!w.layout) w.layout = { x: 0, y: i, w: 12, h: 1 }
        if (widgetHasNumericYAxis(w) || widgetHasNumericXAxis(w)) {
          w.y_axis = w.y_axis && typeof w.y_axis === 'object' ? { ...w.y_axis } : {}
          if (!w.y_axis.tick_format) w.y_axis.tick_format = 'full'
        }
      })
      reflowWidgetLayouts(cfg.widgets)
      cfg.widgets.forEach(w => {
        if (w.id && w.layout) layouts[w.id] = { ...w.layout }
      })
      cfg.layouts = layouts
      return cfg
    },

    // ── Filters ─────────────────────────────────────────────────────
    onFiltersApply(values) {
      this.filterValues = values || {}
    },

    applyFilters(data, values, filters) {
      if (!filters || !filters.length || !values) return data
      let out = data
      for (const f of filters) {
        const v = values[f.column]
        if (!v) continue
        if (f.type === 'date_range') {
          const from = v.from
          const to = v.to
          if (from) out = out.filter(d => String(d[f.column]) >= from)
          if (to)   out = out.filter(d => String(d[f.column]) <= to)
        } else if (f.type === 'single_select') {
          if (v !== '' && v != null) out = out.filter(d => String(d[f.column]) === String(v))
        } else if (Array.isArray(v) && v.length) {
          const set = new Set(v.map(String))
          out = out.filter(d => set.has(String(d[f.column])))
        }
      }
      return out
    },

    // ── Layout ──────────────────────────────────────────────────────
    applyGridContainerWidth(width) {
      const w = width || 0
      if (w <= 0) return
      const nextBp = getGridBreakpointWithHysteresis(w, this.gridBreakpoint)
      const widthChanged = Math.abs(w - this.gridContainerWidth) > 2
      const bpChanged = nextBp !== this.gridBreakpoint
      if (!widthChanged && !bpChanged) return
      if (widthChanged) this.gridContainerWidth = w
      if (bpChanged) this.gridBreakpoint = nextBp
    },

    setupGridResizeObserver() {
      if (typeof ResizeObserver === 'undefined') return
      this.teardownGridResizeObserver()
      if (!this.gridResizeDebounced) {
        this.gridResizeDebounced = _.debounce((w) => this.applyGridContainerWidth(w), 120)
      }
      this.gridResizeObserver = new ResizeObserver((entries) => {
        const w = entries[0] && entries[0].contentRect
          ? entries[0].contentRect.width
          : 0
        this.gridResizeDebounced(w)
      })
      this.$nextTick(() => {
        if (this.$refs.chartGrid) {
          this.gridResizeObserver.observe(this.$refs.chartGrid)
          const rect = this.$refs.chartGrid.getBoundingClientRect()
          this.applyGridContainerWidth(rect.width)
        }
      })
    },
    teardownGridResizeObserver() {
      if (this.gridResizeObserver) {
        this.gridResizeObserver.disconnect()
        this.gridResizeObserver = null
      }
    },
    cellStyle(w) {
      return cellStyleForWidget(w, this.gridBreakpoint)
    },
    // ── Edit mode ───────────────────────────────────────────────────
    toggleEditMode() {
      this.editMode = !this.editMode
      if (!this.editMode) this.selection = null
      this.$nextTick(this.refreshThemeColors)
    },
    closeEditor() {
      this.editMode = false
      this.selection = null
    },
    onKpiClick(i, evt) {
      if (!this.editMode) return
      if (this.cfgDrag && this.cfgDrag.moved) return
      evt && evt.stopPropagation()
      this.selection = { type: 'kpi', index: i }
    },
    onWidgetCellClick(i, evt) {
      if (!this.editMode) return
      if (this.cfgDrag && this.cfgDrag.moved) return
      if (evt) evt.stopPropagation()
      this.selection = { type: 'widget', index: i }
    },
    onCfgReorder(kind, from, to) {
      if (kind === 'kpi') this.reorderKpis(from, to)
      else if (kind === 'widget') this.reorderWidgets(from, to)
    },
    remapSelectionIndex(type, from, to) {
      if (!this.selection || this.selection.type !== type) return
      const idx = remapIndexAfterMove(this.selection.index, from, to)
      this.selection = { type, index: idx }
    },
    onReorderFilters({ from, to }) {
      const list = reorderArray(this.mutableConfig.filters || [], from, to)
      this.$set(this.mutableConfig, 'filters', list)
      this.remapSelectionIndex('filter', from, to)
    },
    reorderKpis(from, to) {
      const list = reorderArray(this.mutableConfig.kpis || [], from, to)
      this.$set(this.mutableConfig, 'kpis', list)
      this.remapSelectionIndex('kpi', from, to)
    },
    reorderWidgets(from, to) {
      const widgets = [...(this.mutableConfig.widgets || [])]
      if (!widgets.length || from === to) return
      const a = widgets[from]
      const b = widgets[to]
      if (a && b) {
        const tmpLayout = { ...(a.layout || {}) }
        a.layout = { ...(b.layout || {}) }
        b.layout = tmpLayout
      }
      const list = reorderArray(widgets, from, to)
      this.$set(this.mutableConfig, 'widgets', list)
      list.forEach(w => this.syncLayoutEntry(w))
      this.remapSelectionIndex('widget', from, to)
    },
    onFilterSelect(i) {
      if (!this.editMode) return
      this.selection = { type: 'filter', index: i }
    },
    onPatchFilter({ index, patch }) {
      const list = this.mutableConfig.filters || []
      const cur = list[index]
      if (!cur) return
      const oldCol = cur.column
      const hadType = cur.type
      const next = { ...cur, ...patch }
      if (Object.prototype.hasOwnProperty.call(patch, 'options')) {
        if (patch.options == null || !Array.isArray(patch.options) || !patch.options.length) {
          delete next.options
        } else {
          next.options = patch.options
        }
      }
      this.$set(this.mutableConfig.filters, index, next)
      if (this.filterValues) {
        if (oldCol && next.column && String(oldCol) !== String(next.column) && this.filterValues[oldCol] != null) {
          const v = this.filterValues[oldCol]
          this.$delete(this.filterValues, oldCol)
          this.$set(this.filterValues, next.column, v)
        }
        if (patch.type != null && hadType && patch.type !== hadType) {
          const c = next.column
          if (c && c in this.filterValues) this.$delete(this.filterValues, c)
        }
      }
    },
    onDeleteFilter(index) {
      const f = (this.mutableConfig.filters || [])[index]
      if (f && f.column && this.filterValues) {
        this.$delete(this.filterValues, f.column)
      }
      const filters = [...(this.mutableConfig.filters || [])]
      filters.splice(index, 1)
      this.$set(this.mutableConfig, 'filters', filters)
      this.selection = null
    },
    onAddFilter() {
      if (!this.mutableConfig.filters) this.$set(this.mutableConfig, 'filters', [])
      const n = { column: 'new_column_' + Date.now(), type: 'multi_select', label: 'New filter' }
      this.mutableConfig.filters.push(n)
      this.selection = { type: 'filter', index: this.mutableConfig.filters.length - 1 }
    },
    syncLayoutEntry(w) {
      if (!w || !w.id || !w.layout) return
      if (!this.mutableConfig.layouts) this.$set(this.mutableConfig, 'layouts', {})
      this.$set(this.mutableConfig.layouts, w.id, { ...w.layout })
    },
    reflowWidgetGrid() {
      const widgets = this.mutableConfig.widgets || []
      if (!widgets.length) return
      const before = widgets.map(w => JSON.stringify(w.layout || {}))
      const changed = reflowWidgetLayouts(widgets)
      if (!changed) return
      widgets.forEach((w, i) => {
        if (JSON.stringify(w.layout || {}) === before[i]) return
        this.$set(this.mutableConfig.widgets, i, { ...w, layout: { ...w.layout } })
        this.syncLayoutEntry(this.mutableConfig.widgets[i])
      })
    },
    onPatchKpi({ index, patch }) {
      const cur = (this.mutableConfig.kpis || [])[index]
      if (!cur) return
      const next = { ...cur, ...patch }
      this.$set(this.mutableConfig.kpis, index, next)
    },
    onPatchWidget({ index, patch }) {
      const w = (this.mutableConfig.widgets || [])[index]
      if (!w) return
      const oldId = w.id
      const next = { ...w, ...patch }
      if (patch.layout) {
        next.layout = { ...(w.layout || {}), ...patch.layout }
      }
      if (Object.prototype.hasOwnProperty.call(patch, 'group_by') && !patch.group_by) {
        this.$delete(next, 'group_by')
      }
      this.$set(this.mutableConfig.widgets, index, next)
      if (oldId && patch.id && oldId !== patch.id && this.mutableConfig.layouts) {
        this.$delete(this.mutableConfig.layouts, oldId)
      }
      this.syncLayoutEntry(next)
      if (patch.layout) this.reflowWidgetGrid()
    },
    onDeleteKpi(index) {
      const kpis = [...(this.mutableConfig.kpis || [])]
      kpis.splice(index, 1)
      this.$set(this.mutableConfig, 'kpis', kpis)
      this.selection = null
    },
    onDeleteWidget(index) {
      const w = (this.mutableConfig.widgets || [])[index]
      if (w && w.id && this.mutableConfig.layouts) {
        this.$delete(this.mutableConfig.layouts, w.id)
      }
      const widgets = [...(this.mutableConfig.widgets || [])]
      widgets.splice(index, 1)
      this.$set(this.mutableConfig, 'widgets', widgets)
      this.selection = null
      this.$nextTick(() => this.reflowWidgetGrid())
    },
    onAddKpi() {
      if (!this.mutableConfig.kpis) this.$set(this.mutableConfig, 'kpis', [])
      const n = {
        label: 'New KPI',
        field: 'cost',
        agg: 'sum',
        format: 'number',
      }
      this.mutableConfig.kpis.push(n)
      this.selection = { type: 'kpi', index: this.mutableConfig.kpis.length - 1 }
    },
    onAddWidget() {
      if (!this.mutableConfig.widgets) this.$set(this.mutableConfig, 'widgets', [])
      let maxRow = 0
      this.mutableConfig.widgets.forEach(ww => {
        const l = ww.layout || { y: 0, h: 1 }
        const b = (Number(l.y) || 0) + (Number(l.h) || 1)
        if (b > maxRow) maxRow = b
      })
      const id = 'w_' + Date.now()
      const w = {
        id,
        type: 'bar',
        x: 'branch',
        y: 'cost',
        agg: 'sum',
        title: 'New chart',
        color: '#3b82f6',
        layout: { x: 0, y: maxRow, w: 6, h: 1 },
      }
      this.mutableConfig.widgets.push(w)
      this.syncLayoutEntry(w)
      this.selection = { type: 'widget', index: this.mutableConfig.widgets.length - 1 }
    },

    // ── Theme (toggle disabled — always light; re-enable if needed) ─
    // toggleTheme() {
    //   this.theme = this.theme === 'dark' ? 'light' : 'dark'
    // },
    refreshThemeColors() {
      this.themeColors = readThemeColors(this.$el)
    },

    onPatchTitle(title) {
      const next = (title || '').trim() || 'Dashboard'
      this.mutableConfig = { ...this.mutableConfig, title: next }
    },

    // ── Save config ─────────────────────────────────────────────────
    saveConfig() {
      const out = _.cloneDeep(this.mutableConfig)
      // Sync layouts map from widget.layout so the patched config round-trips
      out.layouts = {}
      ;(out.widgets || []).forEach(w => {
        if (w.id && w.layout) out.layouts[w.id] = w.layout
      })
      const fname = (out.title || 'dashboard')
        .replace(/[^a-z0-9_-]+/gi, '_').toLowerCase() + '-config.json'
      downloadJson(out, fname)
    },

    /** Temporarily expand scroll-clipped layout so html2canvas captures full content. */
    _beginPdfCaptureLayout(container) {
      const chain = [
        container,
        container.parentElement,
        container.parentElement && container.parentElement.parentElement,
        this.$el,
      ].filter(Boolean)
      const saved = []
      const unlock = {
        overflow: 'visible',
        overflowY: 'visible',
        overflowX: 'visible',
        height: 'auto',
        maxHeight: 'none',
      }
      chain.forEach(el => {
        Object.keys(unlock).forEach(prop => {
          saved.push({ el, prop, value: el.style[prop] })
          el.style[prop] = unlock[prop]
        })
      })
      const scrollTop = container.scrollTop
      container.scrollTop = 0
      return { saved, scrollTop }
    },

    _endPdfCaptureLayout(container, layoutState) {
      if (!layoutState) return
      layoutState.saved.forEach(({ el, prop, value }) => {
        el.style[prop] = value
      })
      container.scrollTop = layoutState.scrollTop
    },

    _patchPdfCaptureClone(clonedDoc) {
      clonedDoc.querySelectorAll('.cfg-dash-container, .cfg-dashboard-main, .cfg-dashboard-layout, .cfg-dashboard-root').forEach(el => {
        el.style.overflow = 'visible'
        el.style.overflowY = 'visible'
        el.style.height = 'auto'
        el.style.maxHeight = 'none'
      })
    },

    // ── PDF export ──────────────────────────────────────────────────
    async exportPDF() {
      if (this.exportingPdf) return
      this.exportingPdf = true
      const container = this.$refs.container
      let layoutState = null
      try {
        const [{ default: html2canvas }, jspdfMod] = await Promise.all([
          import('html2canvas'),
          import('jspdf'),
        ])
        const jsPDF = jspdfMod.jsPDF || jspdfMod.default || jspdfMod

        layoutState = this._beginPdfCaptureLayout(container)
        await this.$nextTick()
        await new Promise(r => requestAnimationFrame(() => requestAnimationFrame(r)))
        await new Promise(r => setTimeout(r, 150))

        const bg = this.themeColors.bg || '#ffffff'
        const captureH = container.scrollHeight
        const captureW = container.scrollWidth
        const canvas = await html2canvas(container, {
          backgroundColor: bg,
          scale: 2,
          useCORS: true,
          logging: false,
          height: captureH,
          width: captureW,
          windowHeight: captureH,
          scrollY: 0,
          scrollX: 0,
          onclone: clonedDoc => this._patchPdfCaptureClone(clonedDoc),
        })

        const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' })
        const pageW = pdf.internal.pageSize.getWidth()
        const pageH = pdf.internal.pageSize.getHeight()
        const mL = 10, mT = 14, mB = 14
        const imgW = pageW - mL * 2
        const imgH = (canvas.height * imgW) / canvas.width

        // Title band on page 1
        pdf.setFontSize(14)
        pdf.setTextColor(30, 41, 59)
        pdf.text(this.title, mL, 10)

        const imgData = canvas.toDataURL('image/png')

        if (imgH <= pageH - mT - mB) {
          pdf.addImage(imgData, 'PNG', mL, mT, imgW, imgH, '', 'FAST')
        } else {
          // Slice across multiple pages
          const pxPerMm = canvas.width / imgW
          const pageContentMm = pageH - mT - mB
          const sliceHPx = Math.floor(pageContentMm * pxPerMm)
          let yPx = 0
          let isFirst = true
          while (yPx < canvas.height) {
            const sliceCanvas = document.createElement('canvas')
            sliceCanvas.width = canvas.width
            sliceCanvas.height = Math.min(sliceHPx, canvas.height - yPx)
            const sctx = sliceCanvas.getContext('2d')
            sctx.fillStyle = bg
            sctx.fillRect(0, 0, sliceCanvas.width, sliceCanvas.height)
            sctx.drawImage(canvas, 0, -yPx)
            const sliceData = sliceCanvas.toDataURL('image/png')
            if (!isFirst) pdf.addPage()
            const sliceMm = sliceCanvas.height / pxPerMm
            pdf.addImage(sliceData, 'PNG', mL, mT, imgW, sliceMm, '', 'FAST')
            yPx += sliceHPx
            isFirst = false
          }
        }

        const fname = (this.title || 'dashboard')
          .replace(/[^a-z0-9_-]+/gi, '_').toLowerCase() + '.pdf'
        pdf.save(fname)
      } catch (err) {
        console.error('[ConfigDashboard] PDF export failed', err)
        // eslint-disable-next-line no-alert
        alert('Failed to export PDF: ' + (err.message || err))
      } finally {
        if (container) this._endPdfCaptureLayout(container, layoutState)
        this.exportingPdf = false
      }
    },
  },
}
</script>

<style scoped>
/* ── Theme variables (dark theme reserved for future use) ──────────── */
/*
.cfg-dashboard-root[data-theme="dark"] {
  --dash-bg: #0f1117;
  --dash-surface: #1e293b;
  --dash-surface2: #273548;
  --dash-text: #e2e8f0;
  --dash-text-muted: #94a3b8;
  --dash-border: #334155;
  --dash-accent: #3b82f6;
  --dash-header-bg: #1e293b;
  --dash-input-bg: #0f1117;
  --dash-input-border: #475569;
  --dash-shadow: rgba(0,0,0,0.4);
}
*/
.cfg-dashboard-root[data-theme="light"],
.cfg-dashboard-root {
  --dash-bg: #f4f6f9;
  --dash-surface: #ffffff;
  --dash-surface2: #f1f5f9;
  --dash-text: #1e293b;
  --dash-text-muted: #64748b;
  --dash-border: #e2e8f0;
  --dash-accent: #3b82f6;
  --dash-header-bg: #ffffff;
  --dash-input-bg: #ffffff;
  --dash-input-border: #cbd5e1;
  --dash-shadow: rgba(0,0,0,0.08);
}

/*
 * Viewport-bounded height + internal scroll (no App.vue changes).
 * Tune --cfg-scroll-top-offset if the bar sits under header/banner/menu.
 */
.cfg-dashboard-root {
  --cfg-scroll-top-offset: 100px;
  display: flex;
  flex-direction: column;
  width: 100%;
  box-sizing: border-box;
  height: calc(100vh - var(--cfg-scroll-top-offset));
  max-height: calc(100vh - var(--cfg-scroll-top-offset));
  min-height: 0;
  overflow: hidden;
  background: var(--dash-bg);
  color: var(--dash-text);
  font-family: system-ui, -apple-system, "Segoe UI", Roboto, sans-serif;
  transition: background 0.3s ease, color 0.3s ease;
}

/* Flex layout: main column (scroll) + editor sidebar */
.cfg-dashboard-layout {
  flex: 1 1 auto;
  display: flex;
  width: 100%;
  min-height: 0;
  max-height: 100%;
  align-items: stretch;
  text-align: left;
}
.cfg-dashboard-main {
  flex: 1 1 auto;
  min-width: 0;
  min-height: 0;
  max-height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.cfg-edit-reorder-hint {
  margin: 0 0 14px 0;
  padding: 10px 14px;
  font-size: .8rem;
  color: var(--dash-text-muted);
  background: color-mix(in srgb, var(--dash-accent) 8%, transparent);
  border: 1px dashed color-mix(in srgb, var(--dash-accent) 35%, transparent);
  border-radius: 8px;
  line-height: 1.45;
}
.cfg-edit-reorder-hint i { margin-right: 6px; color: var(--dash-accent); }
.cfg-drag-hint-icon {
  display: inline-block;
  font-weight: 700;
  color: var(--dash-text);
  letter-spacing: -2px;
  user-select: none;
}
.cfg-drag-handle {
  flex-shrink: 0;
  padding: 4px 6px;
  margin: 0;
  border: 1px solid var(--dash-border);
  border-radius: 6px;
  background: var(--dash-surface2);
  color: var(--dash-text-muted);
  font-size: .75rem;
  font-weight: 700;
  line-height: 1;
  letter-spacing: -1px;
  cursor: grab;
  user-select: none;
  -webkit-user-select: none;
  touch-action: none;
}
.cfg-drag-handle:hover { color: var(--dash-accent); border-color: var(--dash-accent); }
.cfg-drag-handle:active { cursor: grabbing; }
.cfg-drag-handle--widget {
  position: absolute;
  top: 8px;
  right: 8px;
  left: auto;
  z-index: 6;
  box-shadow: 0 2px 8px var(--dash-shadow);
}
.cfg-kpi-reorder-wrap {
  display: flex;
  align-items: stretch;
  gap: 6px;
  min-width: 0;
  flex: 1 1 auto;
}
.cfg-kpi-reorder-wrap .cfg-drag-handle { align-self: center; }
.cfg-kpi-reorder-wrap .cfg-kpi-card { flex: 1; min-width: 0; }
.cfg-reorder-wrap--dragging { opacity: 0.45; pointer-events: none; }
.cfg-reorder-wrap--drop {
  box-shadow: 0 0 0 2px var(--dash-accent) !important;
  outline: 2px solid color-mix(in srgb, var(--dash-accent) 25%, transparent);
}
.cfg-dashboard-root--edit .cfg-kpi-card { cursor: pointer; }
.cfg-dashboard-root--edit .cfg-kpi-row .cfg-kpi-card:hover { filter: brightness(1.02); }
.cfg-chart-grid--edit .cfg-grid-cell {
  cursor: pointer;
  border-radius: 12px;
  transition: box-shadow 0.15s ease, outline 0.15s ease;
}
.cfg-chart-grid--edit .cfg-grid-cell:hover {
  box-shadow: 0 0 0 1px color-mix(in srgb, var(--dash-accent) 40%, transparent);
}
.cfg-grid-cell--sel {
  box-shadow: 0 0 0 2px var(--dash-accent) !important;
  outline: 2px solid color-mix(in srgb, var(--dash-accent) 25%, transparent);
}
.cfg-dashboard-root ::v-deep .cfg-edit-sidebar {
  max-height: 100%;
  min-height: 0;
  align-self: stretch;
}

@media (max-width: 900px) {
  .cfg-dashboard-layout { flex-direction: column; }
  .cfg-dashboard-root ::v-deep .cfg-edit-sidebar {
    width: 100% !important;
    max-height: 50vh;
    border-left: none;
    border-top: 1px solid var(--dash-border);
    box-shadow: none;
  }
}

/* Header (fixed in column; does not scroll with .cfg-dash-container) */
.cfg-dash-header {
  display: flex; align-items: center; gap: 10px;
  padding: 12px 20px;
  background: var(--dash-header-bg);
  border-bottom: 1px solid var(--dash-border);
  position: relative;
  z-index: 50;
  flex-shrink: 0;
  transition: background 0.3s ease;
}
.cfg-dash-title-wrap {
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
  max-width: 60vw;
}
.cfg-dash-title {
  margin: 0; font-size: 1.2rem; font-weight: 700;
  color: var(--dash-text);
  padding: 4px 10px; border-radius: 6px;
  min-width: 0;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}
.cfg-title-cog-btn {
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  padding: 0;
  border: 1px solid var(--dash-border);
  border-radius: 8px;
  background: var(--dash-surface);
  color: var(--dash-text-muted);
  font-size: .95rem;
  cursor: pointer;
  transition: all 0.15s ease;
}
.cfg-title-cog-btn:hover,
.cfg-title-cog-btn--active {
  color: var(--dash-accent);
  border-color: var(--dash-accent);
  background: color-mix(in srgb, var(--dash-accent) 10%, var(--dash-surface));
}
.cfg-title-cog-btn--active i {
  animation: cfgCogSpin 4s linear infinite;
}
@keyframes cfgCogSpin { to { transform: rotate(360deg); } }
.cfg-dash-header-spacer { flex: 1; }

.cfg-header-btn {
  display: inline-flex; align-items: center; gap: 8px;
  height: 34px; padding: 0 14px;
  border-radius: 8px; border: 1px solid var(--dash-border);
  background: var(--dash-surface); color: var(--dash-text);
  font-size: .82rem; font-weight: 600;
  cursor: pointer; transition: all 0.15s ease;
}
.cfg-header-btn:hover:not(:disabled) {
  background: var(--dash-accent); color: #fff;
  border-color: var(--dash-accent);
}
.cfg-header-btn:disabled { opacity: .6; cursor: not-allowed; }

/* Scrollable content (KPIs + charts); header stays visible above */
.cfg-dash-container {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  -webkit-overflow-scrolling: touch;
  padding: 20px;
  max-width: 1600px;
  margin: 0 auto;
  width: 100%;
  box-sizing: border-box;
}

.cfg-dash-loader {
  padding: 80px 20px; text-align: center; color: var(--dash-text-muted);
  display: flex; flex-direction: column; align-items: center; gap: 14px;
}
.cfg-spinner {
  width: 34px; height: 34px;
  border-radius: 50%;
  border: 3px solid var(--dash-surface2);
  border-top-color: var(--dash-accent);
  animation: cfgSpin 0.8s linear infinite;
}
@keyframes cfgSpin { to { transform: rotate(360deg); } }

.cfg-dash-error {
  display: flex; align-items: center; gap: 16px;
  background: color-mix(in srgb, #ef4444 8%, transparent);
  border: 1px solid #ef4444;
  color: #ef4444;
  padding: 14px 20px; border-radius: 10px;
  margin: 14px 0;
}
.cfg-dash-error i { font-size: 1.4rem; }
.cfg-err-title { font-weight: 600; }
.cfg-err-msg { font-size: .85rem; opacity: .8; }
.cfg-dash-error .cfg-header-btn { margin-left: auto; }

/* KPI row */
.cfg-kpi-row {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 14px; margin-bottom: 20px;
}

/* Widget grid — positions from config.json layout (x, y, w, h) on --lg */
.cfg-chart-grid {
  display: grid;
  gap: 16px;
  width: 100%;
  min-width: 0;
}
.cfg-chart-grid--lg {
  grid-template-columns: repeat(12, minmax(0, 1fr));
  grid-auto-rows: 260px;
}
.cfg-chart-grid--md,
.cfg-chart-grid--sm {
  grid-template-columns: minmax(0, 1fr);
  grid-auto-flow: row;
}
.cfg-grid-cell {
  min-width: 0;
  min-height: 0;
  position: relative;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}
.cfg-grid-cell ::v-deep .cfg-chart-card {
  flex: 1 1 auto;
  min-height: 0;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

@media (max-width: 768px) {
  .cfg-dash-header { padding: 10px 14px; flex-wrap: wrap; gap: 8px; }
  .cfg-dash-container { padding: 14px; }
  .cfg-header-btn span { display: none; }
  .cfg-dash-title-wrap { max-width: 100%; }
  .cfg-dash-title { font-size: 1.05rem; }
  .cfg-kpi-row { grid-template-columns: repeat(auto-fit, minmax(140px, 1fr)); gap: 10px; }
  .cfg-chart-grid { gap: 12px; }
}
@media (max-width: 480px) {
  .cfg-dash-container { padding: 10px; }
  .cfg-kpi-row { grid-template-columns: 1fr 1fr; gap: 8px; }
  .cfg-kpi-reorder-wrap { flex-direction: column; align-items: stretch; }
  .cfg-kpi-reorder-wrap .cfg-drag-handle { align-self: flex-start; }
}
</style>

<style>
body.cfg-is-dragging {
  user-select: none;
  -webkit-user-select: none;
  cursor: grabbing;
}
</style>
